import { useEffect, useRef, useMemo } from 'react';
import { useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { getStore } from '../../../stores/StoreContext';
import SessionTrack from '../../../classes/SessionTrack';

/**
 * TrackMonitor - Monitors LiveKit tracks and updates PlayerStore
 * 
 * This component isolates the useTracks() hook to prevent unnecessary re-renders
 * in parent components. LiveKit's useTracks() hook triggers re-renders when
 * room state changes (including audio activity), even if the tracks themselves
 * haven't changed. By isolating it here, only this component re-renders on
 * audio activity.
 * 
 * **Track Stabilization:**
 * Implements track array stabilization by comparing track SIDs. Only updates
 * the PlayerStore when tracks are actually added/removed, not when LiveKit
 * returns new array references for the same tracks.
 * 
 * **Performance Impact:**
 * - Prevents parent Layout component from re-rendering on every audio activity change
 * - Only updates PlayerStore when tracks actually change (by SID)
 * - No UI rendering overhead (returns null)
 * 
 * **Usage:**
 * Place this component inside LiveKitRoom alongside other LiveKit components.
 * It will automatically monitor and sync tracks to the PlayerStore.
 * 
 * @component
 * @returns {null} No UI - monitoring only
 * 
 * @example
 * <LiveKitRoom token={token} serverUrl={url}>
 *   <TrackMonitor />
 *   <SessionLayout />
 * </LiveKitRoom>
 */
function TrackMonitor() {
    const PlayerStore = getStore('playerStore');

    // Subscribe to Camera and ScreenShare tracks from LiveKit
    const livekitTracks = useTracks(
        [
            { source: Track.Source.Camera, withPlaceholder: false },
            { source: Track.Source.ScreenShare, withPlaceholder: false },
        ],
        { onlySubscribed: false }
    );

    /**
     * Stabilize tracks array to prevent unnecessary PlayerStore updates
     * 
     * LiveKit's useTracks() hook returns a new array reference on every render,
     * even when the actual tracks haven't changed. We stabilize the array by
     * comparing track SIDs and only returning a new reference when tracks
     * actually change.
     */
    const stableTracksRef = useRef([]);
    const tracks = useMemo(() => {
        // Create a signature of current tracks based on their SIDs
        const currentSig = livekitTracks.map(t =>
            t.publication?.trackSid || t.participant?.sid || 'unknown'
        ).join(',');

        const prevSig = stableTracksRef.current.map(t =>
            t.publication?.trackSid || t.participant?.sid || 'unknown'
        ).join(',');

        // Only update if tracks actually changed
        if (currentSig !== prevSig) {
            stableTracksRef.current = livekitTracks;
            return livekitTracks;
        }

        // Return same reference if tracks haven't changed
        return stableTracksRef.current;
    }, [livekitTracks]);

    // Convert LiveKit tracks to SessionTrack objects and add to PlayerStore
    useEffect(() => {
        if (!tracks || tracks.length === 0) return;

        tracks.forEach((track) => {
            const sessionTrack = new SessionTrack();
            sessionTrack.populateFromLiveKit(track);
            PlayerStore.addTrack(sessionTrack);
        });
    }, [tracks.length, PlayerStore]);

    // No UI - this is a monitoring component only
    return null;
}

export default TrackMonitor;
